/**
 * EventsList — the model's lifecycle feed: mono timestamp column, status
 * badge, sans detail, hairline separators.
 *
 * Rows come from `GET /api/models/{id}/activity`, composed from what the
 * platform already holds — when the model was created, when each checkpoint
 * was scored and which one is serving, and when the gateway's probes saw it
 * unavailable. There is no event table behind it.
 */

import type { ModelEvent } from '@/api/generated/model'
import { Badge } from '@/components/ui/badge'

const KIND_VARIANT: Record<
  ModelEvent['kind'],
  'success' | 'destructive' | 'outline' | 'secondary'
> = {
  created: 'secondary',
  scored: 'outline',
  promoted: 'success',
  incident: 'destructive',
}

interface EventsListProps {
  events: ModelEvent[]
}

/** "11 Feb · 09:14" — date and time of day, the granularity a feed reads at. */
function formatStamp(iso: string): string {
  const at = new Date(iso)
  const date = at.toLocaleDateString('en-US', { day: '2-digit', month: 'short' })
  const time = at.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${date} · ${time}`
}

export function EventsList({ events }: EventsListProps): React.JSX.Element {
  if (events.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nothing recorded for this model yet.
      </p>
    )
  }
  return (
    <ul>
      {events.map((event) => (
        <li
          key={`${event.at}-${event.kind}-${event.detail}`}
          className="flex items-start gap-4 border-t border-border py-3.5"
        >
          <span className="w-28 shrink-0 pt-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {formatStamp(event.at)}
          </span>
          <Badge variant={KIND_VARIANT[event.kind]} className="shrink-0">
            {event.kind}
          </Badge>
          <span className="text-sm leading-snug">{event.detail}</span>
        </li>
      ))}
    </ul>
  )
}
